import React from 'react';
import { Space, Button, Popconfirm, Tag, Tooltip, Avatar } from 'antd';
import { useTranslation } from 'react-i18next';
import { EyeOutlined, EditOutlined, DeleteOutlined, QuestionCircleOutlined, AppstoreOutlined } from '@ant-design/icons';
import { resendVerificationEmail } from '../UsersServices';
import { message } from 'antd';
import { resolveBackendMessage } from '../../../Utils/apiMessages';
import { MailOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { useErrorHandler } from '../../../Hooks/useErrorHandler';
import SkeletonTable from './SkeletonTable';
import AppTable from '../../Shared/AppTable/AppTable';
import { useGlobal } from '../../Shared/GlobalContext/GlobalContext';
import RowActions from '../../Shared/RowActions/RowActions';

const UserTable = ({
    users,
    loading,
    pagination,
    screens,
    handleTableChange,
    showModal,
    handleDelete,
    showViewModal,
    getUserTokenCall,
    sortBy,
    sortDirection,
    getHighlightClass,
}) => {
    const { user } = useGlobal();
    const { t } = useTranslation();
    const { handleError } = useErrorHandler();
    const [resendLoading, setResendLoading] = useState({});
    const [deleteLoading, setDeleteLoading] = useState({});

    const handleResendVerificationEmail = async (id) => {
        setResendLoading(prev => ({ ...prev, [id]: true }));
        try {
            const payload = await resendVerificationEmail(id);
            const text = resolveBackendMessage(t, payload);
            if (text) message.success(text);
        } catch (error) {
            handleError(error);
        } finally {
            setResendLoading(prev => ({ ...prev, [id]: false }));
        }
    };
    const StatusTag = ({ status, text }) => (
        <Tag color={status ? 'success' : 'error'}>
            {text}
        </Tag>
    );

    // Convert sort_direction ('asc'/'desc') to Ant Design sortOrder ('ascend'/'descend')
    const getSortOrder = (columnSortKey) => {
        if (sortBy === columnSortKey) {
            return sortDirection === 'asc' ? 'ascend' : sortDirection === 'desc' ? 'descend' : null;
        }
        return null;
    };

    const getResponsiveColumns = () => {
        const baseColumns = [
            {
                title: t('id'),
                dataIndex: 'id',
                key: 'id',
                width: 90,
            },
            {
                title: t('picture'),
                dataIndex: 'picture_url',
                key: 'picture_url',
                render: (url, record) => (
                    <Avatar src={url} size={32}>
                        {record?.full_name ? record.full_name.charAt(0).toUpperCase() : '?'}
                    </Avatar>
                ),
            },
            {
                title: t('full_name'),
                dataIndex: 'full_name',
                key: 'full_name',
                sorter: true,
                sortOrder: getSortOrder('full_name'),
                render: (text, record) => (
                    <Space direction="vertical" size={0}>
                        <span>{text}</span>
                        {!screens.sm && (
                            <span style={{ fontSize: '12px', color: '#888' }}>
                                {record.email}
                            </span>
                        )}
                    </Space>
                ),
            },
            {
                title: t('email'),
                dataIndex: 'email',
                key: 'email',
                sorter: true,
                sortOrder: getSortOrder('email'),
            },
            {
                title: t('role'),
                key: 'role',
                sorter: true,
                sortOrder: getSortOrder('is_admin'),
                render: (_, record) => (
                    <span>{record.is_admin ? t('admin') : t('simple_user')}</span>
                ),
            },
            {
                title: t('gender'),
                dataIndex: 'gender',
                key: 'gender',
                sorter: true,
                sortOrder: getSortOrder('gender'),
            },
            {
                title: t('status'),
                key: 'status',
                render: (_, record) => (
                    <Space size={4}>
                        <StatusTag status={!record.is_disabled} text={!record.is_disabled ? t('active') : t('inactive')} />
                        <StatusTag status={record.is_verified} text={record.is_verified ? t('verified') : t('unverified')} />
                    </Space>
                ),
            },
            {
                title: t('actions'),
                key: 'actions',
                render: (_, record) => (
                    <RowActions
                        screens={screens}
                        primary={{
                            label: t('view_user'),
                            icon: <EyeOutlined />,
                            onClick: () => showViewModal(record),
                            tooltip: t('view_user')
                        }}
                        items={[
                            {
                                key: 'edit',
                                label: t('edit_user'),
                                icon: <EditOutlined />,
                                onClick: () => showModal(record)
                            },
                            {
                                key: 'token',
                                label: t('get_token'),
                                icon: <AppstoreOutlined />,
                                onClick: () => getUserTokenCall(record.id),
                                hidden: record.id === user.id
                            },
                            {
                                key: 'resend',
                                label: t('resend_verification_email'),
                                icon: <MailOutlined />,
                                onClick: () => handleResendVerificationEmail(record.id),
                                loading: resendLoading[record.id],
                                hidden: record.is_verified
                            },
                            {
                                key: 'delete',
                                label: t('delete_user'),
                                icon: <DeleteOutlined />,
                                danger: true,
                                confirm: {
                                    title: t('delete_user'),
                                    content: t('are_you_sure_you_want_to_delete_this_user'),
                                    okText: t('yes'),
                                    cancelText: t('no')
                                },
                                onClick: () => handleDelete(record.id),
                                loading: deleteLoading[record.id]
                            }
                        ]}
                    />
                ),
            },
        ];

        return baseColumns;
    };

    const columns = getResponsiveColumns();

    if (loading) {
        return <SkeletonTable columns={columns} rowCount={10} active={true} />;
    }

    return (
        <AppTable
            columns={columns}
            dataSource={users}
            rowKey="id"
            rowClassName={getHighlightClass ? (record) => getHighlightClass(record.id) : undefined}
            scroll={{ x: 800 }}
            pagination={{
                ...pagination,
                showSizeChanger: !screens.xs,
            }}
            onChange={handleTableChange}
        />
    );
};

export default UserTable;
